/**
 * Where sheet (docs/11 §3.4): where to look and how far. Opens from the
 * header chip. Picking a row applies immediately; Done just closes.
 * Radius is four big buttons, not a slider — a driver sets it once with a
 * thumb. Search hits our own venues first, then a geocoder proxied by the
 * API (never a third party from the browser).
 */
import { useEffect, useState } from 'react';
import { Locate, LocateOff, Star, Search, Check, MapPin, Ticket, X } from 'lucide-react';
import { api, type Metro, type Place } from './api.js';
import { useDebounced } from './hooks.js';
import { Sheet, Button } from './ui.js';
import {
  store, MAX_SAVED_PLACES, RADIUS_STEPS_MI, type SavedPlace, type SearchCenter, type SearchContext, type RadiusMi,
} from './storage.js';

export function WhereSheet({ search, locState, bias, metros, count, onChange, onClose }: {
  search: SearchContext;
  locState: 'unknown' | 'granted' | 'denied';
  bias: { lat: number; lng: number } | null;
  metros: Metro[];
  /** Rows inside the current radius, live. */
  count: number | null;
  onChange: (s: SearchContext) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 300);
  const [results, setResults] = useState<Place[]>([]);
  const [searching, setSearching] = useState(false);
  const [saved, setSaved] = useState<SavedPlace[]>(store.places.get());

  useEffect(() => {
    if (dq.length < 2) { setResults([]); setSearching(false); return; }
    let alive = true;
    setSearching(true);
    const near = bias ?? metros[0]?.centroid ?? undefined;
    api.placesSearch(dq, near)
      .then((r) => { if (alive) setResults(r.results); })
      .catch(() => { if (alive) setResults([]); })
      .finally(() => { if (alive) setSearching(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);

  const currentId = search.center.kind === 'place' ? search.center.place.id : 'gps';
  const pick = (c: SearchCenter) => onChange({ ...search, center: c });
  const pickPlace = (p: SavedPlace) => pick({ kind: 'place', place: p });
  const save = (p: SavedPlace) => { const n = [p, ...saved.filter((s) => s.id !== p.id)].slice(0, MAX_SAVED_PLACES); setSaved(n); store.places.set(n); };
  const forget = (id: string) => { const n = saved.filter((s) => s.id !== id); setSaved(n); store.places.set(n); };

  return (
    <Sheet title="Where to look" onClose={onClose} labelledBy="where-title">
      <button className={`option${currentId === 'gps' ? ' selected' : ''}`} aria-pressed={currentId === 'gps'}
        onClick={() => pick({ kind: 'gps' })}>
        {locState === 'denied' ? <LocateOff size={22} aria-hidden="true" /> : <Locate size={22} aria-hidden="true" />}
        <span className="main">
          <span className="l1">Near me</span>
          <span className="l2">{locState === 'denied' ? 'Location is off for Surge' : 'Follows you as you move'}</span>
        </span>
        {currentId === 'gps' && <Check className="check" size={22} aria-hidden="true" />}
      </button>

      {saved.map((p) => (
        <div className="pair" key={p.id}>
          <button className={`option${currentId === p.id ? ' selected' : ''}`} aria-pressed={currentId === p.id}
            onClick={() => pickPlace(p)}>
            <Star size={22} aria-hidden="true" />
            <span className="main">
              <span className="l1">{p.label}</span>
              {p.detail && <span className="l2">{p.detail}</span>}
            </span>
            {currentId === p.id && <Check className="check" size={22} aria-hidden="true" />}
          </button>
          <button className="star on" aria-label={`Remove ${p.label} from saved places`} onClick={() => forget(p.id)}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>
      ))}

      <label className="search">
        <span className="visually-hidden">Venue, address or town</span>
        <Search size={20} aria-hidden="true" />
        <input type="search" inputMode="search" autoComplete="off" placeholder="Venue, address or town"
          value={q} onChange={(e) => setQ(e.target.value)} aria-busy={searching} />
      </label>

      {dq.length >= 2 && !searching && results.length === 0 && (
        <p className="section">Nothing found for “{dq}”.</p>
      )}
      {results.map((p) => {
        const isSaved = saved.some((s) => s.id === p.id);
        return (
          <div className="pair" key={p.id}>
            <button className="option" onClick={() => pickPlace(p)}>
              {p.kind === 'venue' ? <Ticket size={22} aria-hidden="true" /> : <MapPin size={22} aria-hidden="true" />}
              <span className="main">
                <span className="l1">{p.label}</span>
                {p.detail && <span className="l2">{p.detail}</span>}
              </span>
            </button>
            <button className={`star${isSaved ? ' on' : ''}`} aria-pressed={isSaved}
              aria-label={isSaved ? `${p.label} is saved` : `Save ${p.label}`}
              disabled={!isSaved && saved.length >= MAX_SAVED_PLACES}
              onClick={() => (isSaved ? forget(p.id) : save(p))}>
              <Star size={20} aria-hidden="true" />
            </button>
          </div>
        );
      })}

      {dq.length < 2 && (
        <>
          <p className="section">How far</p>
          <div className="radius" role="radiogroup" aria-label="How far to look">
            {RADIUS_STEPS_MI.map((mi) => (
              <button key={mi} role="radio" aria-checked={search.radiusMi === mi}
                onClick={() => onChange({ ...search, radiusMi: mi as RadiusMi })}>
                {mi} mi
              </button>
            ))}
          </div>
          <p className="section" aria-live="polite">
            {count === null ? '' : count === 0
              ? `Nothing within ${search.radiusMi} mi tonight`
              : `${count} crowd${count === 1 ? '' : 's'} within ${search.radiusMi} mi`}
          </p>

          {metros.length > 0 && (
            <>
              <p className="section">Service areas</p>
              <div className="chips">
                {metros.map((m) => (
                  <button className={`chip${currentId === `metro:${m.slug}` ? ' selected' : ''}`} key={m.slug}
                    aria-pressed={currentId === `metro:${m.slug}`}
                    onClick={() => pickPlace({ id: `metro:${m.slug}`, label: m.name, detail: 'Service area', lat: m.centroid.lat, lng: m.centroid.lng })}>
                    {m.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <p className="attrib">Place search © OpenStreetMap contributors</p>
      <Button variant="primary" onClick={onClose}>Done</Button>
    </Sheet>
  );
}
